import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Link, RouterProvider, useRouteError } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import '@fontsource-variable/dm-sans';
import './styles/tokens.css';
import './styles/app.css';
import { ApiError } from './lib/api';
import { AppShell, RequireAuth } from './components/AppShell';
import { GuestProvider } from './components/GuestProvider';
import { ToastProvider } from './components/toast';
import { ClientsPage, ProfilePage, TeamPage } from './pages/AdminPages';
import { ForgotPage, InvitePage, LoginPage, ResetPage } from './pages/AuthPages';
import { CampaignPage, CampaignPreviewPage } from './pages/CampaignPage';
import { CampaignsPage } from './pages/CampaignsPage';
import { ReviewPage } from './pages/ReviewPage';
import { SettingsPage } from './pages/SettingsPage';
import { ShareCampaignPage, ShareLayout } from './pages/SharePages';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
    },
  },
});

function NotFound() {
  return (
    <div className="page page-narrow">
      <span className="eyebrow">404</span>
      <h1 className="page-title" style={{ margin: '10px 0 16px' }}>
        Diese Seite gibt es nicht.
      </h1>
      <Link className="link-arrow" to="/">
        Zur Übersicht
      </Link>
    </div>
  );
}

function RouteError() {
  const error = useRouteError();
  return (
    <div className="page page-narrow">
      <span className="eyebrow">Fehler</span>
      <h1 className="page-title" style={{ margin: '10px 0 16px' }}>
        Hier ist etwas schiefgelaufen.
      </h1>
      <p className="muted" style={{ marginBottom: 20 }}>
        {error instanceof Error ? error.message : 'Unbekannter Fehler'}
      </p>
      <button className="btn btn-primary" onClick={() => window.location.reload()}>
        Neu laden
      </button>
    </div>
  );
}

const router = createBrowserRouter([
  { path: '/anmelden', element: <LoginPage /> },
  { path: '/einladung/:token', element: <InvitePage /> },
  { path: '/passwort-neu/:token', element: <ResetPage /> },
  { path: '/passwort-vergessen', element: <ForgotPage /> },
  {
    path: '/r/:token',
    element: <ShareLayout />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <ShareCampaignPage /> },
      { path: 'motiv/:bannerId', element: <ReviewPage /> },
    ],
  },
  {
    element: (
      <RequireAuth>
        <GuestProvider>
          <AppShell />
        </GuestProvider>
      </RequireAuth>
    ),
    errorElement: <RouteError />,
    children: [
      { path: '/', element: <CampaignsPage /> },
      { path: '/kampagnen/:campaignId', element: <CampaignPage /> },
      { path: '/kampagnen/:campaignId/vorschau', element: <CampaignPreviewPage /> },
      { path: '/kampagnen/:campaignId/motiv/:bannerId', element: <ReviewPage /> },
      { path: '/kunden', element: <ClientsPage /> },
      { path: '/team', element: <TeamPage /> },
      { path: '/profil', element: <ProfilePage /> },
      { path: '/einstellungen', element: <SettingsPage /> },
      { path: '*', element: <NotFound /> },
    ],
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <RouterProvider router={router} />
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
